import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { money } from '@wyocspay/ui';
import { ApiError, api, type SubscriptionChargeView } from './api.js';
import { LOCALES, STRINGS, detectLanguage, stripeLocale, type Lang, type Strings } from './i18n.js';
import { Shell, Panel, PAY_TOKENS } from './Shell.js';
import { LegalModal } from './LegalModal.js';
import { ResultView, type ResultPhase } from './Result.js';
import { ELEMENTS_APPEARANCE, PAYMENT_ELEMENT_LAYOUT } from './stripe-appearance.js';
import { useSecondsLeft } from './useCountdown.js';

const { NAVY, INK, MUTED, FAINT, LINE } = PAY_TOKENS;
const TIER_LABEL: Record<string, string> = { special: 'Special package', vipplus: 'VIP+ package' };

const cache = new Map<string, Promise<Stripe | null>>();
function stripeFor(pk: string): Promise<Stripe | null> {
  let p = cache.get(pk);
  if (!p) { p = loadStripe(pk); cache.set(pk, p); }
  return p;
}

export function SubscriptionPay({ token }: { token: string }) {
  const [lang, setLang] = useState<Lang>(() => {
    const saved = localStorage.getItem('wyocspay.lang') as Lang | null;
    return saved && saved in STRINGS ? saved : detectLanguage();
  });
  const t = STRINGS[lang];
  const locale = LOCALES[lang];

  const [charge, setCharge] = useState<SubscriptionChargeView | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [pk, setPk] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [legal, setLegal] = useState<'terms' | 'privacy' | null>(null);
  const [wstep, setWstep] = useState<1 | 2 | 3>(1);
  const stripePromise = useMemo(() => (pk ? stripeFor(pk) : null), [pk]);

  const bootstrap = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const c = await api.subCharge(token);
      setCharge(c);
      if (c.state === 'SUCCEEDED') { setAlreadyPaid(true); setWstep(3); return; }
      // A charge whose session has run out comes back CANCELLED (the server
      // voids the intent): a dead link, not a payable one.
      if (c.state === 'CANCELLED') {
        setError('This payment session has ended and the link can no longer be used. Please request a new payment link.');
        return;
      }
      const i = await api.subIntent(token);
      setClientSecret(i.clientSecret);
      setPk(i.publishableKey);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { void bootstrap(); }, [bootstrap]);

  const shell = (children: React.ReactNode) => (
    <Shell lang={lang} onLang={setLang} t={t} locale={locale}
      chrome={!loading && !error && charge ? 'full' : 'bare'}
      step={wstep} expiresAt={wstep === 3 ? null : charge?.expiresAt ?? null}
      merchant={charge?.merchantName ?? null} merchantLogo={null} paymentId={charge?.id ?? null}
      assurance={null} onOpenLegal={setLegal}>
      {children}
      {legal ? <LegalModal doc={legal} lang={lang} onClose={() => setLegal(null)} /> : null}
    </Shell>
  );

  if (loading) return shell(<Centred><Spin label={t.loading} /></Centred>);
  if (error) return shell(<Centred><Msg title="This payment link cannot be used">{error}</Msg></Centred>);
  if (alreadyPaid || !charge) {
    return shell(<ResultView phase="ok" authorized amount={charge?.amount ?? null} currency={charge?.currency ?? 'usd'} merchant={charge?.merchantName ?? ''} paymentId={charge?.id ?? null} t={t} locale={locale} />);
  }

  return shell(
    clientSecret && stripePromise ? (
      <Elements stripe={stripePromise} options={{ clientSecret, locale: stripeLocale(lang), appearance: ELEMENTS_APPEARANCE }}>
        <PayForm t={t} locale={locale} charge={charge} token={token} onWizardStep={setWstep} onOpenLegal={setLegal} />
      </Elements>
    ) : <Centred><Spin label={t.loading} /></Centred>,
  );
}

/**
 * Step 1 (method + Stripe's own selector, which carries the real brand logos and
 * handles every method correctly) -> step 2 (confirm) -> step 3 (result). The
 * Payment Element stays mounted (hidden on step 2) so confirmation still has it.
 */
function PayForm({
  t, locale, charge, token, onWizardStep, onOpenLegal,
}: {
  t: Strings; locale: string; charge: SubscriptionChargeView; token: string;
  onWizardStep: (n: 1 | 2 | 3) => void; onOpenLegal: (doc: 'terms' | 'privacy') => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [sub, setSub] = useState<1 | 2>(1);
  const [phase, setPhase] = useState<ResultPhase | null>(null);
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const x = t as unknown as Record<string, string>;
  const billing = charge.kind === 'subscription_renewal' ? (x.billingRenewal ?? 'Monthly renewal') : (x.billingSetup ?? 'First month (setup)');
  const left = useSecondsLeft(charge.expiresAt);
  const expired = left !== null && left <= 0;

  useEffect(() => { onWizardStep(phase ? 3 : sub); }, [phase, sub, onWizardStep]);

  async function pay(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!stripe || !elements || submitting) return;
    if (expired) { setMessage(x.sessionExpiredNote ?? 'This payment session has expired and this link can no longer be used. Please request a new payment link.'); return; }
    if (!accepted) { setMessage(t.acceptRequired); return; }
    setSubmitting(true); setMessage(null);
    setPhase('processing');
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}${window.location.pathname}?returned=1` },
      redirect: 'if_required',
    });
    if (error) { setPhase('declined'); setSubmitting(false); return; }
    setAuthorized(true);
    await pollUntilSettled();
  }

  /**
   * Poll our own state for up to five minutes after the bank authorized.
   *
   * SUCCEEDED and FAILED come only from a verified webhook, so this never
   * invents an outcome: if neither has arrived within the window it stops and
   * says so (timedOut), leaving the payer a "check again" instead of a spinner
   * that never ends or a success that was never confirmed.
   */
  async function pollUntilSettled(): Promise<void> {
    setTimedOut(false);
    const deadline = Date.now() + 5 * 60 * 1000;
    for (;;) {
      try {
        const s = await api.subStatus(token);
        if (s.state === 'SUCCEEDED') { setPhase('ok'); return; }
        if (s.state === 'FAILED' || s.state === 'CANCELLED') { setPhase('declined'); setSubmitting(false); return; }
      } catch { /* keep polling */ }
      if (Date.now() >= deadline) { setTimedOut(true); return; }
      await new Promise((r) => setTimeout(r, 1500));
    }
  }

  function backToForm(toStep: 1 | 2): void {
    setPhase(null); setAuthorized(null); setSubmitting(false); setTimedOut(false); setSub(toStep);
  }

  return (
    <>
      {phase ? (
        <ResultView phase={phase} authorized={authorized} amount={charge.amount} currency={charge.currency}
          merchant={charge.merchantName} paymentId={charge.id} t={t} locale={locale}
          timedOut={phase === 'processing' && timedOut} onRefresh={() => { void pollUntilSettled(); }}
          onRetry={() => backToForm(2)} onChangeMethod={() => backToForm(1)} />
      ) : null}

      <form onSubmit={pay} style={{ display: phase ? 'none' : 'block' }}>
        <div className="sp-cols" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 34, alignItems: 'start' }}>
          <style>{`@media (max-width:820px){ .sp-cols{ grid-template-columns: 1fr !important; } }`}</style>

          <div style={{ display: 'grid', gap: 26 }}>
            <Panel title={x.subPackageTitle ?? 'Your WyoCSPay package'}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16 }}>
                <tbody>
                  <ReqRow label={x.account ?? 'Account'} value={charge.merchantName} bold />
                  <ReqRow label={x.packageLbl ?? 'Package'} value={charge.tier ? (TIER_LABEL[charge.tier] ?? charge.tier) : 'Fee-waiver package'} />
                  <ReqRow label={x.billingLbl ?? 'Billing'} value={billing} />
                  <ReqRow label={t.total} value={money(charge.amount, charge.currency, locale)} mono amount />
                </tbody>
              </table>
            </Panel>

            <div style={{ display: sub === 1 ? 'block' : 'none' }}>
              <Panel title={t.chooseMethod}>
                <PaymentElement onReady={() => setReady(true)} options={{ layout: PAYMENT_ELEMENT_LAYOUT }} />
              </Panel>
            </div>

            {sub === 2 ? (
              <Panel>
                <p style={{ margin: '0 0 14px', fontSize: 15, color: INK }}>{x.reviewLine ?? 'Please review and confirm your payment.'}</p>
                <div style={{ background: '#f7f8f9', border: `1px solid ${LINE}`, padding: '14px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#5cb87f', marginTop: 6, flex: 'none' }} />
                  <span style={{ fontSize: 13.5, color: INK }}>{x.subAuthNote ?? 'By paying you authorize monthly charges for this package until you cancel it.'}</span>
                </div>
              </Panel>
            ) : null}

            {message ? <div style={{ background: '#fff5f5', border: '1px solid #f0c9c9', color: '#a12a2a', padding: '12px 14px', fontSize: 14 }}>{message}</div> : null}
          </div>

          <div style={{ display: 'grid', gap: 16, position: 'sticky', top: 20 }}>
            <div style={{ background: '#fff', border: `1px solid ${LINE}`, boxShadow: 'rgba(38,49,58,0.05) 0 1px 2px' }}>
              <div style={{ padding: '13px 20px', borderBottom: `1px solid #e2e5e8`, fontSize: 12, letterSpacing: '0.09em', textTransform: 'uppercase', color: MUTED, background: '#f7f8f9' }}>{x.orderSummary ?? 'Order summary'}</div>
              <div style={{ padding: '18px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14.5, color: INK, padding: '4px 0' }}>
                  <span style={{ color: MUTED }}>{x.sumSubtotal ?? 'Subtotal'}</span>
                  <span className="wc-num">{money(charge.amount, charge.currency, locale)}</span>
                </div>
                <div style={{ borderTop: `2px solid ${INK}`, margin: '14px 0 0', paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: INK }}>{x.totalDue ?? 'Total due'}</span>
                  <span className="wc-num" style={{ fontSize: 24, fontWeight: 700, color: INK }}>{money(charge.amount, charge.currency, locale)}</span>
                </div>
              </div>
            </div>

            {expired ? (
              <div style={{ background: '#fff5f5', border: '1px solid #f0c9c9', color: '#a12a2a', padding: '12px 14px', fontSize: 13.5, lineHeight: 1.45 }}>
                {x.sessionExpiredNote ?? 'This payment session has expired and this link can no longer be used. Please request a new payment link.'}
              </div>
            ) : sub === 1 ? (
              <button type="button" onClick={() => setSub(2)} disabled={!stripe || !ready} style={btn(btnPrimary, !stripe || !ready)}>{x.continueBtn ?? 'Continue'}</button>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.45, cursor: 'pointer' }}>
                  <input type="checkbox" checked={accepted} onChange={(e) => { setAccepted(e.target.checked); if (e.target.checked) setMessage(null); }} style={{ marginTop: 2, flex: 'none', width: 16, height: 16, accentColor: NAVY }} />
                  <span>{t.acceptPre}{' '}<button type="button" onClick={(e) => { e.preventDefault(); onOpenLegal('terms'); }} style={linkBtn}>{t.terms}</button>{' '}{t.acceptMid}{' '}<button type="button" onClick={(e) => { e.preventDefault(); onOpenLegal('privacy'); }} style={linkBtn}>{t.privacy}</button>{t.acceptPost}</span>
                </label>
                <button type="submit" disabled={!stripe || submitting || !accepted} style={btn(btnPrimary, !stripe || submitting || !accepted)}>{submitting ? t.paying : `${t.payNow} ${money(charge.amount, charge.currency, locale)}`}</button>
                <button type="button" onClick={() => setSub(1)} disabled={submitting} style={btnGhost}>{x.changeMethod ?? 'Change method'}</button>
              </div>
            )}
            <p style={{ margin: 0, fontSize: 12, color: FAINT, lineHeight: 1.45 }}>{x.subPaidTo ?? 'Paid to Wyoming Cloud Solutions LLC through Stripe.'}</p>
          </div>
        </div>
      </form>
    </>
  );
}

const btnPrimary: React.CSSProperties = { width: '100%', background: NAVY, color: '#fff', border: 'none', padding: '14px 20px', fontSize: 15.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
const btnGhost: React.CSSProperties = { width: '100%', background: '#fff', color: INK, border: `1px solid ${LINE}`, padding: '13px 20px', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
/** A button that visibly greys out (not just `disabled`) when its guard is unmet. */
function btn(base: React.CSSProperties, disabled: boolean): React.CSSProperties {
  return disabled ? { ...base, opacity: 0.45, cursor: 'not-allowed' } : base;
}
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, color: '#2f6188', textDecoration: 'underline', font: 'inherit', cursor: 'pointer' };

function ReqRow({ label, value, bold, mono, amount }: { label: string; value: string; bold?: boolean; mono?: boolean; amount?: boolean }) {
  return (
    <tr>
      <td style={{ padding: amount ? '15px 0 3px' : '8px 0', color: MUTED, width: 170, verticalAlign: 'top', borderTop: amount ? `2px solid ${INK}` : undefined, fontWeight: amount ? 700 : 400 }}>{label}</td>
      <td className={mono ? 'wc-num' : undefined} style={{ padding: amount ? '15px 0 3px' : '8px 0', color: INK, fontWeight: bold || amount ? 700 : 500, borderTop: amount ? `2px solid ${INK}` : undefined }}>{value}</td>
    </tr>
  );
}

function Centred({ children }: { children: React.ReactNode }) {
  return <div style={{ flex: 1, minHeight: 240, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px 0' }}><div style={{ width: '100%', maxWidth: 520 }}>{children}</div></div>;
}
function Spin({ label }: { label: string }) {
  return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}><span style={{ width: 34, height: 34, border: `3px solid ${LINE}`, borderTopColor: NAVY, borderRadius: '50%', animation: 'sp-spin .9s linear infinite' }} /><span style={{ color: MUTED }}>{label}</span><style>{`@keyframes sp-spin{to{transform:rotate(360deg)}}`}</style></div>;
}
function Msg({ title, children }: { title: string; children: React.ReactNode }) {
  return <div style={{ background: '#fff5f5', border: '1px solid #f0c9c9', padding: '16px 18px' }}><div style={{ fontWeight: 700, color: '#a12a2a', marginBottom: 4 }}>{title}</div><div style={{ fontSize: 14, color: INK }}>{children}</div></div>;
}
